import { useBooleanFlagValue } from '@openfeature/react-sdk';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';
import { useGetDefaultRibbonFilters } from 'src/pages/assets/useGetDefaultRibbonFilters';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetAssetsRegister } from '@/hooks/queries';
import { getCounter } from '@/utils/collectionUtils';
import { addAssetUrl } from '@/utils/urls';

import { AssetRegisterToolbar } from './AssetRegisterToolbar';
import { useGetCollectionTableProps } from './config';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'assets' });
  const objectToolbarEnabled = useBooleanFlagValue('object_toolbar', false);
  const navigate = useNavigate();
  const title = st('register_title');
  const { data, loading } = useGetAssetsRegister({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.asset);
  const counter = getCounter(tableProps.totalItemsCount, loading);
  // The object toolbar wraps its counter in parentheses itself, so hand it the
  // raw count — `getCounter` (used for the legacy PageLayout counter above) is
  // already parenthesised and would double-wrap (CUS-157).
  const toolbarCounter =
    loading || tableProps.totalItemsCount === undefined
      ? undefined
      : String(tableProps.totalItemsCount);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'assets.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-157) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <AssetRegisterToolbar
            title={title}
            counter={toolbarCounter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            create={{
              label: st('createNewButton'),
              onClick: () => navigate(addAssetUrl()),
            }}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission
            permission={'insert:asset'}
            canHaveAccessAsContributor={true}
          >
            <Button variant={'primary'} href={addAssetUrl()}>
              {st('createNewButton')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.Asset}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
    </PageLayout>
  );
};

export default Page;
